// src/encounter/lib/to-referral-order.ts
// Map a referral capture (caller-supplied target + in-dialog additional-diagnoses chips + a
// free-text note) → a DS PlanOrder attached to a Dx on the AssessmentPlanCanvas + the structured
// CodedOrderPayload kept in the app-side side-map (order-payload.ts), keyed by orderId.
//
// CONTRACT VERIFIED (PROD-1299, live dev-api 2026-06-22): PatientAssessment.referrals[] IS the
// top-level wire key, and each AssessmentReferral carries diagnosis[]/otherDiagnosis[] + notes +
// summary + a referral TARGET that is a DoctorList — a REAL provider the patient is referred to
// (Flutter AssessmentReferral.fromDoctorList), NOT a free string. A fabricated target does NOT
// round-trip (CreateAssessment 500s, the referral is dropped). So a FUNCTIONAL referral needs a
// provider-search picker (DoctorList) — net-new UI, tracked as PROD-1302; until then this v1 keeps
// only the note + additional diagnoses + a display-name target (the +Referral order cannot save a
// valid referral without the picker). The Flutter toJson articleUrl bug is NOT reproduced;
// assessmentPlanArticles is a separate patient-education feature, not referrals.
//
// The caller supplies orderId (unique per attachment — the same referral target can legitimately
// be ordered against more than one Dx, so the id is NOT derived from the target). No
// Math.random/Date.now in module scope (see order-payload conventions).
import type { CodeSearchResult, PlanOrder } from '@scripsteam/scrips-signal-ds';
import type { CodedOrderPayload, OrderCoding } from './order-payload';

/** The referral target, supplied by the caller (contract-uncertain — name/id only in v1). */
export interface ReferralTargetInput {
  /** Display name of the target (specialty / provider / clinic) — used for the label + payload. */
  name: string;
  /** Optional coded id of the target. */
  code?: string;
  system?: string;
}

/** PROD-1395 / PROD-3336: the practitioner picked for a speciality referral. */
export interface ReferralPractitionerInput {
  /** The practitioner's id (guid). */
  id: string;
  name: string;
}

export interface ReferralCapture {
  /** Optional referral target (default: note-only referral). */
  target?: ReferralTargetInput;
  /**
   * PROD-1395: the role ('dietitian'). Since PROD-3336 the dialog passes it only with a dietetics speciality (or a saved
   * PROD-1395 dietitian referral). With a role the payload carries `performerRole`
   * (lower case), `targetPractitionerId` (null = any of the role) and `targetDisplay`; `target` is set to the display.
   */
  role?: { code: string; name: string };
  /**
   * PROD-3336 (`referrals` flag): the clinic speciality the referral goes to. The payload carries `targetSpecialityId` /
   * `targetSpecialityName`, `targetPractitionerId` (null = anyone of the speciality) and `targetDisplay`; a dietetics
   * speciality also carries `performerRole: 'dietitian'` (pass it as `role`), so the readers that filter on it still work.
   */
  speciality?: { id: string; name: string };
  /** The optional practitioner of that role or speciality. */
  practitioner?: ReferralPractitionerInput | null;
  /** Additional / other diagnoses picked from the in-dialog search (removable chips). */
  additionalDiagnoses?: CodeSearchResult[];
  /** Free-text referral note. */
  note?: string;
}

export interface ReferralOrder {
  planOrder: PlanOrder;
  payload: Extract<CodedOrderPayload, { kind: 'referral' }>;
}

/** A DS CodeSearchResult (display/code/system) → the side-map OrderCoding (displayName/code/system). */
function toOrderCoding(r: CodeSearchResult): OrderCoding {
  return {
    code: r.code ?? '',
    displayName: r.display,
    ...(r.system ? { system: r.system } : {}),
  };
}

/** Patient keeps at most 128 characters of a speciality name. */
const SPECIALITY_NAME_MAX = 128;

export function toReferralOrder(capture: ReferralCapture, orderId: string): ReferralOrder {
  const specialityId = capture.speciality?.id.trim();
  const specialityName = capture.speciality?.name.trim().slice(0, SPECIALITY_NAME_MAX);
  const roleName = specialityName || capture.role?.name.trim();
  const targetDisplay = roleName ? capture.practitioner?.name.trim() || roleName : undefined;
  const targetName = targetDisplay ?? capture.target?.name?.trim();
  const label = targetName || 'Referral';

  const additionalDiagnoses = (capture.additionalDiagnoses ?? []).map(toOrderCoding);
  const note = capture.note?.trim();

  const payload: Extract<CodedOrderPayload, { kind: 'referral' }> = {
    kind: 'referral',
    ...(targetName ? { target: targetName } : {}),
    ...(capture.role && targetDisplay ? { performerRole: capture.role.code.trim().toLowerCase() } : {}),
    ...((capture.role || specialityId) && targetDisplay
      ? { targetPractitionerId: capture.practitioner?.id ?? null, targetDisplay }
      : {}),
    ...(specialityId && specialityName ? { targetSpecialityId: specialityId, targetSpecialityName: specialityName } : {}),
    ...(additionalDiagnoses.length ? { additionalDiagnoses } : {}),
    ...(note ? { note } : {}),
  };

  return {
    // dxIds empty — the host attaches the target Dx when it appends the order (v2 PROD-1422).
    planOrder: { id: orderId, type: 'referral', label, dxIds: [] },
    payload,
  };
}
